import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PRODUCTS } from "@/data/products";
import { INDUSTRIES } from "@/data/industries";

const NAV = [
  { label: "Products", to: "/products", children: PRODUCTS.map((p) => ({ label: p.name, to: `/products/${p.slug}` })) },
  { label: "Industries", to: "/industries", children: INDUSTRIES.map((i) => ({ label: i.name, to: `/industries/${i.slug}` })) },
  { label: "Technical Services", to: "/technical-services" },
  { label: "Elastomers", to: "/elastomers" },
  { label: "Quality", to: "/quality" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Dropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const enter = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { timer.current = setTimeout(() => setOpen(false), 150); };
  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-link`}
        className={({ isActive }) =>
          `block px-1 py-2 font-body text-[13px] font-medium tracking-wide uppercase whitespace-nowrap ${isActive ? "text-amber-brand" : "text-steel hover:text-steel-light"}`
        }
        aria-expanded={open}
        aria-haspopup="true"
      >
        {item.label}
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full pt-2 z-50"
          >
            <div className="bg-ink-2 border border-line min-w-[260px] shadow-2xl shadow-black/60">
              <Link to={item.to} className="block px-5 py-3 border-b border-line font-mono-tech text-[11px] uppercase tracking-[0.2em] text-amber-brand hover:bg-ink-3" data-testid={`dropdown-all-${item.label.toLowerCase()}`}>
                All {item.label} →
              </Link>
              {item.children.map((child) => (
                <Link
                  key={child.to}
                  to={child.to}
                  data-testid={`dropdown-link-${child.to.split("/").pop()}`}
                  className="block px-5 py-2.5 text-sm text-steel hover:text-steel-light hover:bg-ink-3 border-b border-line/40 last:border-0"
                >
                  {child.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header className={`fixed top-0 inset-x-0 z-[100] border-b transition-colors duration-300 ${scrolled ? "bg-ink/90 backdrop-blur-xl border-line" : "bg-ink/60 backdrop-blur-md border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="header-logo-link" className="flex-none" aria-label="Vikas Rolls — home">
          <img src="/images/logo.png" alt="Vikas Rolls Private Limited logo" className="h-14 md:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center flex-1 justify-evenly gap-x-3 mx-6" aria-label="Main navigation">
          {NAV.map((item) =>
            item.children ? (
              <Dropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-1 py-2 font-body text-[13px] font-medium tracking-wide uppercase whitespace-nowrap ${isActive ? "text-amber-brand" : "text-steel hover:text-steel-light"}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <Link
          to="/request-a-quote"
          data-testid="header-quote-button"
          className="hidden lg:inline-flex group items-center gap-2 bg-amber-brand text-ink font-display font-bold text-sm px-5 py-2.5 hover:bg-steel-light flex-none"
        >
          Request a Quote
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>

        <button
          className="lg:hidden p-2 text-steel-light"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open-button"
          aria-label="Open navigation menu"
        >
          <Menu size={26} />
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[110] bg-ink flex flex-col lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-line">
              <img src="/images/logo.png" alt="Vikas Rolls logo" className="h-14 w-auto" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close-button" aria-label="Close navigation menu" className="p-2 text-steel-light">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-8" aria-label="Mobile navigation links">
              {NAV.map((item) => (
                <div key={item.label} className="border-b border-line/60">
                  {item.children ? (
                    <>
                      <button
                        className="w-full flex items-center justify-between py-4 font-display font-bold text-2xl text-steel-light"
                        onClick={() => setMobileGroup(mobileGroup === item.label ? null : item.label)}
                        data-testid={`mobile-nav-${item.label.toLowerCase()}-toggle`}
                        aria-expanded={mobileGroup === item.label}
                      >
                        {item.label}
                        <ChevronDown size={20} className={`transition-transform duration-200 ${mobileGroup === item.label ? "rotate-180 text-amber-brand" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileGroup === item.label && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                            <Link to={item.to} className="block py-2 pl-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand">All {item.label} →</Link>
                            {item.children.map((child) => (
                              <Link key={child.to} to={child.to} className="block py-2.5 pl-4 text-steel hover:text-steel-light">
                                {child.label}
                              </Link>
                            ))}
                            <div className="h-3" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link to={item.to} className="block py-4 font-display font-bold text-2xl text-steel-light" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}-link`}>
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to="/request-a-quote"
                data-testid="mobile-quote-button"
                className="mt-8 flex items-center justify-center gap-3 bg-amber-brand text-ink font-display font-bold text-lg px-6 py-4"
              >
                Request a Quote <ArrowRight size={18} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
